import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { extractErrorMessage } from '../../api/errors';
import { UserSubscription, useReleasePlanChange, useSubscriptionsForUser } from '../../queries/userSubscriptions';
import { CancelSubscriptionDialog } from './CancelSubscriptionDialog';
import { SchedulePlanChangeDialog } from './SchedulePlanChangeDialog';
import { EditSubscriptionDialog } from './EditSubscriptionDialog';

interface SubscriptionsTabProps {
  userId: string;
}

const DATE_FORMATTER = new Intl.DateTimeFormat('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });

function formatDate(value: string | null): string {
  return value ? DATE_FORMATTER.format(new Date(value)) : '—';
}

const STATUS_CLASS: Record<string, string> = {
  Active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400',
  Cancelled: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400',
  Expired: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400',
  'Past Due': 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400',
  Unpaid: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400',
  Trialing: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400',
  Promotional: 'bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-400',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASS[status] ?? 'bg-muted text-muted-foreground'}`}>
      {status}
    </span>
  );
}

function SubscriptionCard({ subscription }: { subscription: UserSubscription }) {
  const [dialog, setDialog] = useState<'cancel' | 'change-plan' | 'edit' | null>(null);
  const [releaseError, setReleaseError] = useState<string | null>(null);
  const release = useReleasePlanChange(subscription.id);

  const handleRelease = () => {
    setReleaseError(null);
    release.mutate(undefined, { onError: (err) => setReleaseError(extractErrorMessage(err, 'Failed to release the pending change.')) });
  };

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold">{subscription.package_name ?? subscription.package_code}</h3>
          <StatusBadge status={subscription.billing_status} />
        </div>
        <div className="flex gap-2">
          {/* Edit is a direct DB edit (package/dates/active/auto-renew) - available
              on any subscription so an expired or comp one can be fixed/re-activated. */}
          <button
            type="button"
            onClick={() => setDialog('edit')}
            className="rounded-md border border-input px-2.5 py-1 text-xs font-medium hover:bg-accent"
          >
            Edit
          </button>
          {subscription.is_active && (
            <>
              {subscription.stripe_subscription_id && (
                <button
                  type="button"
                  onClick={() => setDialog('change-plan')}
                  className="rounded-md border border-input px-2.5 py-1 text-xs font-medium hover:bg-accent"
                >
                  Change plan
                </button>
              )}
              <button
                type="button"
                onClick={() => setDialog('cancel')}
                className="rounded-md border border-red-200 px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-50 dark:border-red-900 dark:text-red-400 dark:hover:bg-red-950/30"
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>

      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm sm:grid-cols-4">
        <div>
          <dt className="text-xs text-muted-foreground">Billing cycle</dt>
          <dd>{subscription.billing_cycle ?? '—'}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Start date</dt>
          <dd>{formatDate(subscription.start_date)}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">End date</dt>
          <dd>{formatDate(subscription.end_date)}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Auto-renew</dt>
          <dd>{subscription.auto_renew ? 'Yes' : 'No'}</dd>
        </div>
      </dl>

      {subscription.pending_package_code && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-md bg-muted px-3 py-2 text-sm">
          <span>
            Plan change to <span className="font-medium">{subscription.pending_package_code}</span> (
            {subscription.pending_billing_cycle}) effective {formatDate(subscription.pending_effective_date)}
          </span>
          <button
            type="button"
            onClick={handleRelease}
            disabled={release.isPending}
            className="text-xs font-medium text-primary hover:underline disabled:opacity-50"
          >
            {release.isPending ? 'Releasing…' : 'Release change'}
          </button>
        </div>
      )}
      {releaseError && <p className="mt-2 text-sm text-destructive">{releaseError}</p>}

      {dialog === 'cancel' && (
        <CancelSubscriptionDialog
          subscriptionId={subscription.id}
          hasStripeSubscription={!!subscription.stripe_subscription_id}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === 'change-plan' && (
        <SchedulePlanChangeDialog
          subscriptionId={subscription.id}
          currentPackageCode={subscription.package_code}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === 'edit' && <EditSubscriptionDialog subscription={subscription} onClose={() => setDialog(null)} />}
    </div>
  );
}

export function SubscriptionsTab({ userId }: SubscriptionsTabProps) {
  const { data, isLoading } = useSubscriptionsForUser(userId);
  const subscriptions = data?.subscriptions ?? [];

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (subscriptions.length === 0) {
    return <div className="py-8 text-center text-muted-foreground">No subscriptions for this user.</div>;
  }

  // Current = still switched on (what Cancel and Change plan act on); the rest
  // are the user's history: cancelled, expired or replaced subscriptions.
  const current = subscriptions.filter((s) => s.is_active);
  const previous = subscriptions.filter((s) => !s.is_active);

  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-muted-foreground">Current</h2>
        {current.length === 0 ? (
          <p className="text-sm text-muted-foreground">No current subscription.</p>
        ) : (
          current.map((s) => <SubscriptionCard key={s.id} subscription={s} />)
        )}
      </section>
      {previous.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-muted-foreground">Previous subscriptions ({previous.length})</h2>
          {previous.map((s) => (
            <SubscriptionCard key={s.id} subscription={s} />
          ))}
        </section>
      )}
    </div>
  );
}
